import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Image as ImageIcon,
  Search as SearchIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { deactivateConsignorItem, fetchConsignorInventory } from '../api/consignorInventory';
import type { ConsignorInventoryItem, ItemCondition } from '../types/consignorInventory';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso: string | null) => {
  if (!iso) return null;
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(iso));
};

const PICKUP_DAYS = 5;

function daysRemaining(deactivatedAt: string): number {
  const deadline = new Date(deactivatedAt);
  deadline.setDate(deadline.getDate() + PICKUP_DAYS);
  const diff = deadline.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

// ─── Condition chip ───────────────────────────────────────────────────────────

function ConditionChip({ condition }: { condition: ItemCondition }): React.ReactElement {
  const isNew = condition === 'new';
  return (
    <Chip
      label={isNew ? 'Nuevo' : 'Usado'}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.04em',
        bgcolor: isNew ? '#F0FDF4' : '#F8FAFC',
        color: isNew ? '#15803D' : '#475569',
        border: '1px solid',
        borderColor: isNew ? '#BBF7D0' : '#CBD5E1',
      }}
    />
  );
}

// ─── Filter type ──────────────────────────────────────────────────────────────

type ConditionFilter = 'all' | 'new' | 'used';

const FILTER_OPTIONS: { value: ConditionFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'new', label: 'Nuevos' },
  { value: 'used', label: 'Usados' },
];

// ─── Item card ────────────────────────────────────────────────────────────────

interface ItemCardProps {
  item: ConsignorInventoryItem;
  onDeactivate: (item: ConsignorInventoryItem) => void;
}

function InventoryItemCard({ item, onDeactivate }: ItemCardProps): React.ReactElement {
  const isDeactivated = item.status === 'deactivated';
  const days = item.deactivatedAt ? daysRemaining(item.deactivatedAt) : null;

  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        mb: 1.5,
        border: '1px solid',
        borderColor: isDeactivated ? 'rgba(251,191,36,0.4)' : 'rgba(226,232,240,1)',
        borderRadius: 2,
        bgcolor: isDeactivated ? 'rgba(255,251,235,0.5)' : 'white',
      }}
    >
      {/* Image */}
      <Box
        sx={{
          width: 88,
          height: 88,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'rgba(248,250,252,1)',
          border: '1px solid rgba(226,232,240,1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.imageUrl ? (
          <Box
            component="img"
            src={item.imageUrl}
            alt={item.productName}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
          />
        ) : (
          <ImageIcon sx={{ color: 'action.disabled', fontSize: 32 }} />
        )}
      </Box>

      {/* Info */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ lineHeight: 1.3, flex: 1, textAlign: 'left' }}
            >
              {item.productName || 'Sin título'}
            </Typography>
            <ConditionChip condition={item.condition} />
          </Box>
          {item.sizeName && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.25, display: 'block', textAlign: 'left' }}
            >
              Talla: {item.sizeName}
            </Typography>
          )}
          {item.artId && (
            <Typography
              variant="caption"
              color="text.disabled"
              fontFamily="monospace"
              sx={{ display: 'block', textAlign: 'left' }}
            >
              UU{item.artId}
            </Typography>
          )}
        </Box>

        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1.5 }}
        >
          {/* Status info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {isDeactivated ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ fontSize: 14, color: '#B45309' }} />
                <Typography variant="caption" fontWeight={700} sx={{ color: '#B45309' }}>
                  {days === 0
                    ? '¡Último día para recoger!'
                    : `${days} ${days === 1 ? 'día' : 'días'} para recoger`}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: '#15803D', fontWeight: 600 }}>
                En venta
              </Typography>
            )}
            {item.receivedAt && !isDeactivated && (
              <Typography variant="caption" color="text.disabled">
                Ingresó {fmtDate(item.receivedAt)}
              </Typography>
            )}
          </Box>

          {/* Action */}
          {!isDeactivated && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => onDeactivate(item)}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#DC2626',
                borderColor: 'rgba(220,38,38,0.35)',
                borderRadius: 1.5,
                px: 1.5,
                py: 0.5,
                minWidth: 0,
                '&:hover': {
                  bgcolor: 'rgba(220,38,38,0.05)',
                  borderColor: '#DC2626',
                },
              }}
            >
              Retirar
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  );
}

// ─── Deactivate confirm dialog ────────────────────────────────────────────────

interface DeactivateDialogProps {
  item: ConsignorInventoryItem | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeactivateDialog({
  item,
  saving,
  onCancel,
  onConfirm,
}: DeactivateDialogProps): React.ReactElement {
  return (
    <Dialog
      open={item != null}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, mx: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>¿Retirar este par?</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {item && (
          <Typography variant="body2" color="text.secondary">
            Se marcará como retirado:{' '}
            <Box component="span" fontWeight={700} color="text.primary">
              {item.productName}
            </Box>
            {item.sizeName ? ` — Talla ${item.sizeName}` : ''}.
          </Typography>
        )}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: '#FFFBEB',
            border: '1px solid rgba(251,191,36,0.4)',
            display: 'flex',
            gap: 1,
          }}
        >
          <WarningIcon sx={{ fontSize: 18, color: '#B45309', flexShrink: 0, mt: 0.1 }} />
          <Typography variant="caption" sx={{ color: '#92400E', lineHeight: 1.5 }}>
            Tienes <strong>{PICKUP_DAYS} días</strong> para recoger este par en la tienda. Pasado
            ese tiempo, volverá a estar a la venta automáticamente.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={saving}
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: '#111827',
            '&:hover': { bgcolor: '#374151' },
          }}
        >
          {saving ? 'Retirando…' : 'Confirmar retiro'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ConsignorInventoryPage(): React.ReactElement {
  const [items, setItems] = useState<ConsignorInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [conditionFilter, setConditionFilter] = useState<ConditionFilter>('all');
  const [deactivateTarget, setDeactivateTarget] = useState<ConsignorInventoryItem | null>(null);
  const [deactivateSaving, setDeactivateSaving] = useState(false);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await fetchConsignorInventory(search || undefined);
    setLoading(false);
    if (!result.ok) {
      setLoadError(result.error);
      setItems([]);
      return;
    }
    setItems(result.data.items);
  }, [search]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(value), 450);
  }, []);

  const handleDeactivateConfirm = useCallback(async () => {
    if (!deactivateTarget) return;
    setDeactivateSaving(true);
    const result = await deactivateConsignorItem(deactivateTarget.id);
    setDeactivateSaving(false);
    setDeactivateTarget(null);
    if (!result.ok) {
      setSnackMessage(`Error: ${result.error}`);
      return;
    }
    setSnackMessage('Par marcado como retirado. Tienes 5 días para recogerlo.');
    load().catch(() => {});
  }, [deactivateTarget, load]);

  const filtered = items.filter((item) => {
    if (conditionFilter === 'new') return item.condition === 'new';
    if (conditionFilter === 'used') return item.condition === 'used';
    return true;
  });

  const activeCount = items.filter((i) => i.status === 'active').length;

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 2, pt: 3, pb: 12 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
          Mi inventario
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {loading
            ? '…'
            : `${activeCount} ${activeCount === 1 ? 'par en venta' : 'pares en venta'}`}
        </Typography>
      </Box>

      {/* Search */}
      <TextField
        size="small"
        fullWidth
        placeholder="Buscar por nombre…"
        value={searchInput}
        onChange={handleSearchChange}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'rgba(248,250,252,1)' },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, overflowX: 'auto', pb: 0.5 }}>
        {FILTER_OPTIONS.map((f) => {
          const active = conditionFilter === f.value;
          return (
            <Chip
              key={f.value}
              label={f.label}
              onClick={() => setConditionFilter(f.value)}
              size="small"
              sx={{
                fontWeight: active ? 700 : 500,
                bgcolor: active ? '#111827' : 'white',
                color: active ? 'white' : 'text.secondary',
                border: '1px solid',
                borderColor: active ? '#111827' : 'rgba(226,232,240,1)',
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { bgcolor: active ? '#374151' : 'rgba(0,0,0,0.04)' },
              }}
            />
          );
        })}
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {/* Items */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: '#111827' }} />
        </Box>
      )}

      {!loading && filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary" fontWeight={600}>
            Sin artículos
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            {search
              ? 'Ningún artículo coincide con tu búsqueda.'
              : 'Aún no tienes pares consignados.'}
          </Typography>
        </Box>
      )}

      {!loading &&
        filtered.length > 0 &&
        filtered.map((item) => (
          <InventoryItemCard key={item.id} item={item} onDeactivate={setDeactivateTarget} />
        ))}

      {/* Deactivate dialog */}
      <DeactivateDialog
        item={deactivateTarget}
        saving={deactivateSaving}
        onCancel={() => setDeactivateTarget(null)}
        onConfirm={() => {
          handleDeactivateConfirm().catch(() => {});
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackMessage != null}
        autoHideDuration={5000}
        onClose={() => setSnackMessage(null)}
        message={snackMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
